import { useState } from 'react';
import api from '../api/axios';

/**
 * Custom hook for managing tour creation and editing forms
 * Handles form state, submission, and image management
 */
export const useTourForm = (agency, setTours, setToast, t, systemConfig) => {
    const [isCreateModalOpen, setIsCreateModalOpen] = useState(false);
    const [isEditModalOpen, setIsEditModalOpen] = useState(false);
    const [uploadProgress, setUploadProgress] = useState(0);
    const [editingTour, setEditingTour] = useState(null);

    const [createForm, setCreateForm] = useState({
        title: '',
        property_id: '',
        price: '',
        currency: systemConfig?.currency || '',
        property_type: '',
        size: '',
        rooms: '',
        floor: '',
        has_elevator: false,
        has_solar: false,
        city: '',
        area: '',
        description: '',
        show_price: true,
        property_images: []
    });

    const [editForm, setEditForm] = useState({
        title: '',
        price: '',
        currency: '',
        property_type: '',
        size: '',
        rooms: '',
        floor: '',
        has_elevator: false,
        has_solar: false,
        city: '',
        area: '',
        description: '',
        show_price: true,
        og_title: '',
        og_description: '',
        og_image: ''
    });

    // Create Handlers
    const handleCreateClick = async () => {
        let suggestedId = '';
        try {
            const res = await api.get('/tours/generate-id');
            suggestedId = res.data.property_id;
        } catch (err) {
            console.error('Failed to generate property ID:', err);
        }

        setCreateForm({
            title: '',
            property_id: suggestedId,
            price: '',
            currency: systemConfig?.currency || '',
            property_type: '',
            size: '',
            rooms: '',
            floor: '',
            has_elevator: false,
            has_solar: false,
            city: '',
            area: '',
            description: '',
            show_price: true,
            property_images: []
        });
        setIsCreateModalOpen(true);
    };

    const handleCreateChange = (e) => {
        const { name, value, type, checked, files } = e.target;
        if (type === 'file') {
            if (name === 'property_images') {
                setCreateForm(prev => ({ ...prev, [name]: Array.from(files) }));
            }
        } else {
            setCreateForm(prev => ({
                ...prev,
                [name]: type === 'checkbox' ? checked : value
            }));
        }
    };

    const handleCreateSubmit = async (e) => {
        e.preventDefault();


        const formData = new FormData();
        formData.append('title', createForm.title);
        formData.append('property_id', createForm.property_id);
        formData.append('agency_id', agency.id);

        if (createForm.price) formData.append('price', createForm.price);
        if (createForm.currency) formData.append('currency', createForm.currency);
        if (createForm.property_type) formData.append('property_type', createForm.property_type);
        if (createForm.size) formData.append('size', createForm.size);
        if (createForm.rooms) formData.append('rooms', createForm.rooms);
        if (createForm.floor) formData.append('floor', createForm.floor);
        formData.append('has_elevator', createForm.has_elevator);
        formData.append('has_solar', createForm.has_solar);
        if (createForm.city) formData.append('city', createForm.city);
        if (createForm.area) formData.append('area', createForm.area);
        if (createForm.description) formData.append('description', createForm.description);
        formData.append('show_price', createForm.show_price);

        if (createForm.property_images.length > 0) {
            createForm.property_images.forEach(file => {
                formData.append('property_images', file);
            });
        }

        try {
            setUploadProgress(0);
            const res = await api.post('/tours/upload', formData, {
                onUploadProgress: (progressEvent) => {
                    const percentCompleted = Math.round((progressEvent.loaded * 100) / progressEvent.total);
                    setUploadProgress(percentCompleted);
                }
            });

            // Add the new tour directly to the state for immediate display
            setTours(prevTours => [res.data, ...prevTours]);

            setIsCreateModalOpen(false);
            setUploadProgress(0);
            setToast({ isVisible: true, message: t('listingCreatedSuccess') || "Listing created successfully!", type: 'success' });
        } catch (err) {
            console.error(err);
            setUploadProgress(0);
            setToast({ isVisible: true, message: err.response?.data?.detail || t('createFailed') || "Failed to create listing", type: 'error' });
        }
    };

    // Edit Handlers
    const handleEditClick = (tour) => {
        setEditingTour(tour);
        setEditForm({
            title: tour.title,
            property_id: tour.property_id || '',
            price: tour.price || '',
            currency: tour.currency || '',
            property_type: tour.property_type || '',
            size: tour.size || '',
            rooms: tour.rooms || '',
            floor: tour.floor || '',
            has_elevator: tour.has_elevator,
            has_solar: tour.has_solar,
            city: tour.city || '',
            area: tour.area || '',
            description: tour.description || '',
            show_price: tour.show_price !== false,  // Default to true if not set
            og_title: tour.og_title || '',
            og_description: tour.og_description || '',
            og_image: tour.og_image || ''
        });
        setIsEditModalOpen(true);
    };

    const handleEditChange = (e) => {
        const { name, value, type, checked } = e.target;
        setEditForm(prev => ({
            ...prev,
            [name]: type === 'checkbox' ? checked : value
        }));
    };

    const handleEditSubmit = async (e) => {
        e.preventDefault();
        try {
            // Convert numeric fields to proper types
            const payload = {
                ...editForm,
                price: editForm.price ? parseFloat(editForm.price) : null,
                rooms: editForm.rooms ? parseInt(editForm.rooms) : null,
                floor: editForm.floor ? parseInt(editForm.floor) : null
            };

            const res = await api.put(`/client/tours/${editingTour.id}`, payload);

            setTours(prevTours => prevTours.map(t => t.id === editingTour.id ? res.data : t));
            setIsEditModalOpen(false);
            setToast({
                isVisible: true,
                message: t('updateSuccess'),
                type: 'success'
            });
        } catch (err) {
            console.error('Failed to update tour:', err);
            setToast({
                isVisible: true,
                message: t('errorUpdatingTour'),
                type: 'error'
            });
        }
    };

    // Image Management Handlers (Edit Mode)
    const handleDeleteImage = async (imagePath) => {
        if (!confirm(t('confirmDeleteImage') || "Are you sure you want to delete this image?")) return;
        try {
            const formData = new FormData();
            formData.append('image_path', imagePath);

            // Use generic request for DELETE with body
            await api.request({
                method: 'DELETE',
                url: `/tours/${editingTour.id}/images`,
                data: formData,
                headers: { 'Content-Type': 'multipart/form-data' }
            });

            // Update local state
            const currentImages = editingTour.property_images ? JSON.parse(editingTour.property_images) : [];
            const updatedImages = currentImages.filter(img => img !== imagePath);
            const updatedImagesJson = JSON.stringify(updatedImages);

            setEditingTour(prev => ({ ...prev, property_images: updatedImagesJson }));
            setTours(prev => prev.map(t => t.id === editingTour.id ? { ...t, property_images: updatedImagesJson } : t));

            setToast({ isVisible: true, message: t('imageDeleted') || "Image deleted", type: 'success' });
        } catch (err) {
            console.error(err);
            setToast({ isVisible: true, message: t('imageDeleteFailed') || "Failed to delete image", type: 'error' });
        }
    };

    const handleAddImages = async (e) => {
        const files = Array.from(e.target.files);
        if (files.length === 0) return;

        const formData = new FormData();
        formData.append('property_id', editingTour.property_id);
        formData.append('agency_id', agency.id);
        files.forEach(file => formData.append('property_images', file));

        try {
            // We use upload_tour endpoint to append images
            // Note: We don't send 'file' (zip), so it won't overwrite the tour
            const res = await api.post('/tours/upload', formData);

            // Update local state
            setEditingTour(prev => ({ ...prev, property_images: res.data.property_images }));
            setTours(prev => prev.map(t => t.id === editingTour.id ? { ...t, property_images: res.data.property_images } : t));

            setToast({ isVisible: true, message: t('imagesAdded') || "Images added successfully", type: 'success' });
            e.target.value = ''; // Reset input
        } catch (err) {
            console.error(err);
            setToast({ isVisible: true, message: t('imagesAddFailed') || "Failed to add images", type: 'error' });
        }
    };

    return {
        isCreateModalOpen,
        setIsCreateModalOpen,
        isEditModalOpen,
        setIsEditModalOpen,
        uploadProgress,
        createForm,
        editForm,
        editingTour,
        setEditingTour,
        handleCreateClick,
        handleCreateChange,
        handleCreateSubmit,
        handleEditClick,
        handleEditChange,
        handleEditSubmit,
        handleDeleteImage,
        handleAddImages
    };
};
